import { useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { useFeature } from '../featureFlags/FeatureGate';
import { FEATURE_PUBLICATION_EXCEPTIONS } from '../publications/publicationExceptionsApi';

/**
 * Uprawnienia nadawane imiennie na koncie (makieta back office v2, fala B,
 * decyzja D9 użytkownika 20.10.2026) — lustro listy z API
 * (`shared/permission-grants.ts`). Klucze to dane.
 */
export const PERMISSION_GRANTS: Array<{ key: string; label: string; description: string }> = [
  {
    key: 'PUBLICATION_EXCEPTION_APPROVE',
    label: 'Akceptacja wyjątku od zakazu podwykonawcy',
    description:
      'Zatwierdza albo odrzuca wnioski traderów o wyjątek od bramki publikacji — gdy klient zakazuje podwykonawców albo trader nie może potwierdzić warunków. Administratorzy mają to uprawnienie rolą.',
  },
];

/** Najkrótszy powód zmiany — jak w API. */
export const MIN_GRANT_REASON_LENGTH = 10;

export async function savePermissionGrants(userId: number | string, grants: string[], reason: string) {
  const { data } = await axiosNest.put(`/users/${userId}/permission-grants`, { grants, reason });
  return data;
}

/**
 * „Uprawnienia imienne” na ekranie konta — niezależnie od roli. Zmiana
 * z powodem (co najmniej 10 znaków), zapis idzie osobno od formularza
 * konta i trafia do dziennika działań: co nadano, co odebrano, powód.
 * Za przełącznikiem `publicationExceptionRequests` — wyłączony: sekcji nie ma.
 */
export const PermissionGrantsSection = () => {
  const on = useFeature(FEATURE_PUBLICATION_EXCEPTIONS);
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const current = Array.isArray(record?.permissionGrants) ? (record?.permissionGrants as string[]) : [];
  const [selected, setSelected] = useState<string[]>(current);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const currentKey = current.join(',');

  useEffect(() => {
    setSelected(currentKey ? currentKey.split(',') : []);
  }, [currentKey]);

  if (!on || !record) return null;

  const changed =
    selected.length !== current.length || selected.some((k) => !current.includes(k));
  const toggle = (key: string, checked: boolean) =>
    setSelected((s) => (checked ? [...s.filter((k) => k !== key), key] : s.filter((k) => k !== key)));

  const save = async () => {
    setBusy(true);
    try {
      await savePermissionGrants(record.id, selected, reason.trim());
      notify('Uprawnienia imienne zapisane — zmiana jest w dzienniku działań.', { type: 'success' });
      setReason('');
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="permission-grants">
      <Typography variant="subtitle2">Uprawnienia imienne</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        Nadawane tej osobie niezależnie od roli — np. z imiennej listy od Marathonu. Każda zmiana wymaga powodu
        i trafia do dziennika działań.
      </Typography>
      {PERMISSION_GRANTS.map((g) => (
        <Box key={g.key} sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.includes(g.key)}
                onChange={(e) => toggle(g.key, e.target.checked)}
                slotProps={{ input: { 'aria-label': g.label } }}
                data-testid={`grant-${g.key}`}
              />
            }
            label={g.label}
          />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 4 }}>
            {g.description}
          </Typography>
        </Box>
      ))}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }} useFlexGap>
        <TextField
          size="small"
          label={`Powód zmiany (co najmniej ${MIN_GRANT_REASON_LENGTH} znaków)`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ minWidth: 320 }}
          slotProps={{ htmlInput: { 'data-testid': 'grant-reason' } }}
        />
        <Button
          variant="outlined"
          size="small"
          data-testid="grant-save"
          disabled={busy || !changed || reason.trim().length < MIN_GRANT_REASON_LENGTH}
          onClick={() => void save()}
        >
          Zapisz uprawnienia
        </Button>
      </Stack>
    </Box>
  );
};
